import {
    Box,
    Skeleton,
    SxProps,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableHead,
    TableRow,
    tableRowClasses,
    Theme,
    Typography,
} from '@mui/material'
import { FeatureFlagIdSuperDirectory } from 'c1gen/features'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { ProfileTypeAvatar } from 'components/core/avatar/ProfileTypeAvatar'
import { DisplayNameDetails } from 'components/core/display-name-details'
import { useFeatureCheckerLazy } from 'components/no-ui/capability'
import { removeDoubleQuotes } from 'components/no-ui/string-formatting'
import { routes } from 'lib/routes'
import { ProfileType } from 'pbts/c1/api/profiletype/v1/profiletype'
import { SearchUserAttributesWithProfileTypeSourceRequest } from 'pbts/c1/api/user/v1/user'
import { Struct, Value } from 'pbts/google/protobuf/struct'
import { FormattedMessage } from 'react-intl'
import { searchUserAttributesWithProfileTypeSourceApi } from 'redux_toolkit/users'
import { ProfileStyles } from './styles'

const ProfileRowHeader = ({ showSourceColumn }: { showSourceColumn?: boolean }): React.ReactElement => <TableRow>
    <TableCell sx={ProfileStyles.headerCell}>
        <Typography variant="h6" component="span">
            <FormattedMessage id="components_profile_list_Key" defaultMessage="Attribute" />
        </Typography>
    </TableCell>
    <TableCell sx={ProfileStyles.headerCell}>
        <Typography variant="h6" component="span">
            <FormattedMessage id="components_profile_list_value" defaultMessage="Value" />
        </Typography>
    </TableCell>
    {showSourceColumn && (
        <TableCell sx={ProfileStyles.headerCell}>
            <Typography variant="h6" component="span">
                <FormattedMessage id="components_profile_list_profile_types" defaultMessage="Source profile type" />
            </Typography>
        </TableCell>
    )}
</TableRow>

type ProfileRowProps = {
    field?: ProfileField
    profileTypes?: ProfileType[]
    showSourceColumn?: boolean
}

const ProfileRow = ({ field, profileTypes, showSourceColumn }: ProfileRowProps): React.ReactElement => {
    if (!field) {
        return <TableRow>
            <TableCell>
                <Skeleton />
            </TableCell>
            <TableCell>
                <Skeleton />
            </TableCell>
            {showSourceColumn && <TableCell>
                <Skeleton />
            </TableCell>}
        </TableRow>
    }

    return <TableRow
        key={field.key}
        hover
        sx={{
            [` .${tableCellClasses.root}`]: {
                '&:first-child': {
                    borderTopLeftRadius: C1BorderRadius.regular,
                    borderBottomLeftRadius: C1BorderRadius.regular,
                },
                '&:last-child': {
                    borderTopRightRadius: C1BorderRadius.regular,
                    borderBottomRightRadius: C1BorderRadius.regular,
                },
            },
            [`&.${tableRowClasses.hover}:hover`]: {
                backgroundColor: 'primary.extraLight',
            },
        }}
    >
        <TableCell sx={ProfileStyles.profileKey}>
            <Typography variant="monospace">{field.key}</Typography>
        </TableCell>
        <TableCell sx={ProfileStyles.profileValue}>
            <Typography>
                {removeDoubleQuotes(Value.toJsonString(field.value, { prettySpaces: 2 }))}
            </Typography>
        </TableCell>
        {showSourceColumn && <TableCell sx={ProfileStyles.profileValue}>
            {profileTypes?.map((profileType) => (
                <Box>
                    <DisplayNameDetails
                        avatar={
                            <ProfileTypeAvatar
                                profileTypeName={profileType.name}
                                profileTypeId={profileType.id}
                                size="sm"
                            />}
                        displayName={profileType.name}
                        link={routes.userDataSources.profileTypes.detail(profileType.id)}
                    />
                </Box>
            ))}
        </TableCell>}
    </TableRow>
}

type ProfileField = {
    key: string
    value: Value
    profileTypes: ProfileType[]
}

type ProfileListProps = {
    title?: string
    profile?: Struct
    header?: boolean
    tableSx?: SxProps<Theme>
    userId?: string
    showProfileTypeSources?: boolean
}

export const ProfileList = ({ title, profile, header = true, tableSx = {}, userId, showProfileTypeSources = false }: ProfileListProps):
    React.ReactElement => {
    const hasFeature = useFeatureCheckerLazy()
    const hasSuperDirectoryFF = hasFeature(FeatureFlagIdSuperDirectory)
    const showSourceColumn = (showProfileTypeSources && hasSuperDirectoryFF) ?? false
    let fields: ProfileField[] = []

    const { data } = searchUserAttributesWithProfileTypeSourceApi.useSearchUserAttributesWithProfileTypeSource(
        SearchUserAttributesWithProfileTypeSourceRequest.create({ userId }),
        { actionable: showSourceColumn && userId !== '' },
    )

    if (showSourceColumn && data?.list) {
        fields = data.list.map((field) => {
            return {
                key: field.profileAttributeKey,
                value: Value.create({ kind: { oneofKind: 'stringValue', stringValue: field.profileAttributeValue } }),
                profileTypes: field.profileTypes,
            }
        })
    } else if (profile) {
        fields = Object.entries(profile.fields).map(([key, value]) => ({
            key,
            value,
            profileTypes: [],
        }))
        fields.sort((first, second) => 0 - (first.key > second.key ? -1 : 1))
    }

    return <>
        {
            title && <Typography variant="h5" component="div" py={2}>
                {title}
            </Typography>
        }
        <Table size="small" sx={tableSx}>
            {
                header && <TableHead>
                    <ProfileRowHeader showSourceColumn={showSourceColumn} />
                </TableHead>
            }
            <TableBody>
                {fields.map((field) =>
                    <ProfileRow
                        key={field.key}
                        field={field}
                        profileTypes={field.profileTypes}
                        showSourceColumn={showSourceColumn}
                    />)}
            </TableBody>
        </Table>
    </>
}
